<template>
    <SkeletonLayout />
</template>

<script setup>
import { onMounted, onUnmounted, inject, provide, watch } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'primevue/usetoast'
import { useI18n } from 'vue-i18n'

import SkeletonLayout from '@/layouts/Skeleton.vue'
import { Capacitor, SystemBars, SystemBarsStyle } from '@capacitor/core'
import { App as CapApp } from '@capacitor/app'
// import { SplashScreen } from '@capacitor/splash-screen'
import { StatusBar, Style } from '@capacitor/status-bar'
import sensorPlugin from '@/plugins/sensor'
import useRecordingLiveActivity from '@/lib/hooks/use-recording-live-activity'
import { registerPush, unregisterPush } from '@/lib/push'
import { rearmReminders } from '@/lib/local-notifications'
import { sendRecordingIntents } from '@/lib/helpers/recording-intents'
import { sendPolarCaptures } from '@/lib/helpers/polar'

const isMobile = inject('isMobile')
const isBuildForMobile = inject('isBuildForMobile')

const store = useStore()
const { t, locale } = useI18n()
const toast = useToast()
provide('toast', toast)

const config = {
    language: locale,
    handlers: {
        onProcessLocked: () => {
            toast.add({ severity: 'warn', summary: t('sensor.onProcessLocked'), life: 3000 }) //'There is already a process, please wait'
        },
        onGetSessionsListFailed: () => {
            toast.add({ severity: 'warn', summary: t('sensor.onGetSessionsListFailed'), life: 3000 }) //'Failed to read the sessions list'
        },
        onGetSessionFailed: () => {
            toast.add({ severity: 'warn', summary: t('sensor.onGetSessionFailed'), life: 3000 }) //'Failed to read the session'
        },
        onUploadFailed: (file) => {
            toast.add({
                severity: 'warn',
                summary: t('sensor.onUploadFailed', { filename: file.properties.filename }),
                life: 4000,
            }) //'Failed to upload the session '
        },
        onUploadSourceMissingRedownloading: () => {
            toast.add({
                severity: 'info',
                summary: t('sensor.retry-upload-redownloading'),
                life: 4000,
            })
        },
        onNativeFeatureUnavailable: () => {
            toast.add({
                severity: 'warn',
                summary: t('sensor.native-feature-unavailable'),
                life: 3000,
            })
        },
        onNativeSyncMissingDevice: () => {
            toast.add({
                severity: 'warn',
                summary: t('sensor.native-sync-missing-device'),
                life: 5000,
            })
        },
        onNativeSyncFailed: (error) => {
            toast.add({
                severity: 'warn',
                summary: t('sensor.native-sync-failed'),
                detail: error?.message || String(error),
                life: 7000,
            })
        },
    },
}
sensorPlugin.configure(config)

// Native Live Activity (iOS) / ongoing notification (Android) mirroring the live
// recording metrics. Hosted at the app root so it spans the whole recording
// regardless of route changes; a no-op on web / unsupported platforms.
useRecordingLiveActivity()

let userRefreshInFlight = false
const refreshAuthenticatedUser = async () => {
    if (userRefreshInFlight || !store.getters['auth/loggedIn']) return

    const token = store.state.auth.token
    const userId = store.state.auth.user?.id
    const tutorialStageAtRequest = store.state.auth.user?.interactive_tutorial_stage
    if (!token || !userId) return

    userRefreshInFlight = true
    try {
        const user = await store.dispatch('auth/user', { token })
        if (String(user?.id) === String(userId) && store.state.auth.token === token) {
            const currentTutorialStage = store.state.auth.user?.interactive_tutorial_stage
            await store.dispatch('auth/setUser', {
                ...user,
                interactive_tutorial_stage:
                    currentTutorialStage !== tutorialStageAtRequest
                        ? currentTutorialStage
                        : user.interactive_tutorial_stage,
            })
        }
    } finally {
        userRefreshInFlight = false
    }
}

const handleVisibilityChange = () => {
    if (!document.hidden) void refreshAuthenticatedUser()
}

// P7: what the phone still has to send (MOVE PRO launches, Polar captures), at
// each start of the app, each return to it and each return of the network.
const sendPending = () => {
    if (!store.getters['auth/loggedIn']) return
    void sendRecordingIntents()
    void sendPolarCaptures()
}

watch(() => store.getters['auth/loggedIn'], sendPending, { immediate: true })

onMounted(() => {
    if (Capacitor.getPlatform() === 'web') {
        document.addEventListener('visibilitychange', handleVisibilityChange)
    }
    window.addEventListener('online', sendPending)
})

onUnmounted(() => {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    window.removeEventListener('online', sendPending)
})

// Push notifications (native only): register the device's FCM token once the
// user is authenticated, re-sync it on every foreground, and deactivate it on
// logout. registerPush() is idempotent and never throws.
if (Capacitor.getPlatform() !== 'web') {
    watch(
        () => store.getters['auth/loggedIn'],
        (logged) => {
            if (logged) registerPush()
            else unregisterPush()
        },
        { immediate: true }
    )
    // Local reminders (e.g. 7-day inactivity): arm on cold boot, then re-arm on
    // every foreground so the countdown resets each time the app is opened.
    // appStateChange does NOT fire on first launch, hence this initial call.
    rearmReminders()
    CapApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
            rearmReminders()
            sendPending()
            if (store.getters['auth/loggedIn']) {
                registerPush()
                void refreshAuthenticatedUser()
            }
        }
    })
}

function openStore() {
    const platform = Capacitor.getPlatform()
    const storeUrl =
        platform === 'android' ? import.meta.env.VITE_APP_PLAYSTORE_URL : import.meta.env.VITE_APP_APPSTORE_URL
    if (storeUrl) {
        window.location.href = storeUrl
    }
}

const checkIfAppInstalled = () => {
    const platform = Capacitor.getPlatform()
    const isNativeApp = typeof Capacitor.isNativePlatform === 'function' ? Capacitor.isNativePlatform() : false

    if (isNativeApp) {
        return
    }

    if (platform !== 'android' && platform !== 'ios') {
        return
    }

    const scheme = import.meta.env?.VITE_APP_URLSCHEME
    if (!scheme) {
        openStore()
        return
    }

    const url = new URL(window.location.href)
    const deepLinkTarget = `${scheme}://${url.pathname}${url.search}`

    let fallbackTriggered = false

    const fallbackTimeout = setTimeout(() => {
        fallbackTriggered = true
        openStore()
    }, 1500)

    const clearListeners = () => {
        clearTimeout(fallbackTimeout)
        document.removeEventListener('visibilitychange', handleVisibilityChange)
    }

    function handleVisibilityChange() {
        if (document.hidden) {
            clearListeners()
        }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Attempt to open the application via a temporary iframe element
    const iframe = document.createElement('iframe')
    iframe.style.display = 'none'
    iframe.src = deepLinkTarget
    document.body.appendChild(iframe)

    setTimeout(() => {
        document.body.removeChild(iframe)
        if (!fallbackTriggered) {
            clearListeners()
        }
    }, 2000)
}

// Logic related to the splash screen.
onMounted(async () => {
    console.log(`### DEEPLINK tests ### isBuildForMobile:${isBuildForMobile} ; is:${Capacitor.getPlatform()}`)
    if (!window.location.href.includes('pricing')) {
        if (
            !isBuildForMobile &&
            isMobile &&
            (Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios')
        ) {
            console.log('### DEEPLINK redirection ###')
            // alert('### DEEPLINK redirection ###')
            await checkIfAppInstalled()
        }
    }

    // +--------------------------------------+
    // |  🔋 📶 🕒       ← Status Bar        |
    // |--------------------------------------|
    // |                                      |
    // |          ← app (webview)             |
    // |                                      |
    // |--------------------------------------|
    // |  ◀️   ◼️   🗂️     ← Navigation Bar   |
    // +--------------------------------------+
    // Android: Capacitor's own system bars, which also pad the webview on
    // older Chromium (< 140) whose safe area values are wrong.
    if (Capacitor.getPlatform() === 'android') {
        await SystemBars.setStyle({ style: SystemBarsStyle.Dark })
        await SystemBars.show()
    } else if (Capacitor.isPluginAvailable('StatusBar')) {
        StatusBar.setStyle({ style: Style.Dark })

        StatusBar.setOverlaysWebView({ overlay: true }) // Ajouté aussi pour iOS

        StatusBar.show()
    }
    //idea is to use store.state.auth.initialCheckDone and hide quickly when app is loaded
    // if (Capacitor.isPluginAvailable('SplashScreen')) {
    // SplashScreen.hide()
    // }
})
</script>
